import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { dbHelpers } from '../lib/supabase';
import { fetchSessions } from '../services/supabaseService';
import { trackAgendaRemove } from '../utils/analytics';
import AacButton from '../components/aac/AacButton';
import { useConferenceYear } from '../contexts/ConferenceYearContext';
import { useListNavigation } from '../hooks/useListNavigation';

// `start_time` is a time-of-day column ("14:30:00"), not a full datetime —
// it must be combined with `date` ("2026-09-14") to sort/compare correctly.
const getSessionDateTime = (session) => {
  if (session.date && session.start_time) {
    const parsed = new Date(`${session.date}T${session.start_time}`);
    if (!isNaN(parsed)) return parsed;
  }
  return null;
};

const MyAgenda = () => {
  const navigate = useNavigate();
  const { announce, focusFirstHeading } = useAccessibility();
  const { activeYear } = useConferenceYear();
  const [sessions, setSessions] = useState([]);
  const [bookmarkedSessions, setBookmarkedSessions] = useState(new Set());
  const [agendaSessions, setAgendaSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('schedule'); // 'schedule' or 'list'
  const [selectedDay, setSelectedDay] = useState('all');

  const loadData = useCallback(async () => {
    try {
      // Load bookmarked sessions from Supabase
      const { data: bookmarkIds, error: bookmarksError } = await dbHelpers.getUserBookmarks();

      if (bookmarksError) {
        console.error('❌ Error loading bookmarks:', bookmarksError);
        announce('Error loading bookmarks');
        setError('Failed to load bookmarks');
        setLoading(false);
        return;
      }

      setBookmarkedSessions(new Set(Array.isArray(bookmarkIds) ? bookmarkIds : []));

      if (!bookmarkIds || bookmarkIds.length === 0) {
        setLoading(false);
        return;
      }

      // Load session details from Supabase using fetchSessions for proper data transformation
      const data = await fetchSessions(activeYear);

      if (!data || !Array.isArray(data)) {
        console.error('❌ Invalid sessions data received:', data);
        announce('Error loading session details');
        setError('Failed to load sessions');
        return;
      }

      // fetchSessions() already guarantees a display-ready `.time` string
      // (formatSessionTime() in supabaseService.js), so no further
      // derivation is needed here.
      setSessions(data);

    } catch (error) {
      console.error('❌ Error loading agenda data:', error);
      announce('Error loading agenda');
      setError('Failed to load agenda');
    } finally {
      setLoading(false);
    }
  }, [announce, activeYear]);

  const filterAgendaSessions = useCallback(() => {
    // Ensure bookmarkedSessions is always a Set to prevent TypeError
    const safeBookmarkedSessions = bookmarkedSessions instanceof Set ? bookmarkedSessions : new Set();
    
    // Filter sessions to only include bookmarked ones
    const bookmarked = sessions.filter(session => {
      return safeBookmarkedSessions.has(session.id);
    });

    // Filter by selected day if not 'all'
    let filtered = bookmarked;
    if (selectedDay !== 'all') {
      filtered = bookmarked.filter(session => session.date === selectedDay);
    }

    // Sort by date + start time. `start_time` alone is just a time-of-day
    // column ("14:30:00"), not a full datetime, so it must be combined
    // with `date` to sort correctly.
    const sorted = filtered.sort((a, b) => {
      const dateTimeA = getSessionDateTime(a);
      const dateTimeB = getSessionDateTime(b);
      if (dateTimeA && dateTimeB) return dateTimeA - dateTimeB;
      if (dateTimeA) return -1;
      if (dateTimeB) return 1;
      return 0;
    });

    setAgendaSessions(sorted);
  }, [sessions, bookmarkedSessions, selectedDay]);

  useEffect(() => {
    focusFirstHeading();
    announce('My Agenda page loaded');

    loadData();
  }, [announce, focusFirstHeading, loadData]);

  useEffect(() => {
    filterAgendaSessions();
  }, [filterAgendaSessions]);

  const removeFromAgenda = async (sessionId) => {
    try {
      const { error } = await dbHelpers.removeBookmark(sessionId);

      if (error) {
        console.error('❌ Error removing bookmark:', error);
        announce('Error removing session from agenda');
        return;
      }

      // Track the removal action with enhanced analytics
      const session = sessions.find(s => s.id === sessionId);
      const sessionTitle = session?.title || 'Unknown Session';
      trackAgendaRemove(sessionId, sessionTitle);
      // Tracked agenda removal from My Agenda page

      // Update local state only if Supabase operation succeeded
      const newBookmarks = new Set(bookmarkedSessions);
      newBookmarks.delete(sessionId);
      setBookmarkedSessions(newBookmarks);
      announce('Session removed from agenda');

    } catch (error) {
      console.error('❌ Error removing bookmark:', error);
      announce('Error removing session from agenda');
    }
  };

  const { currentItem: currentNavSession } = useListNavigation(agendaSessions, {
    onOpen: (session) => navigate(`/session/${session.session_number || session.id}`),
    onToggleAgenda: (session) => removeFromAgenda(session.id),
    getLabel: (session) => session.title,
    getId: (session) => session.id,
  });
  const currentNavSessionId = currentNavSession ? currentNavSession.id : null;

  // Scroll/focus the current list-nav session only when it actually changes
  // (via N/P), not on every unrelated re-render. An inline ref callback
  // fires on every render (it's a fresh function each time), which meant
  // any state change elsewhere on the page caused this card to immediately
  // scrollIntoView/focus itself again, making the page look "stuck".
  const sessionRefs = useRef(new Map());
  useEffect(() => {
    if (!currentNavSessionId) return;
    const el = sessionRefs.current.get(currentNavSessionId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    el.focus({ preventScroll: true });
  }, [currentNavSessionId]);

  const exportAgenda = () => {
    const agendaText = agendaSessions.map(session => {
      // `start_time`/`end_time` are time-of-day only ("09:00:00") — pass
      // the combined `date` + time to formatTime/formatDate, not the bare
      // time alone, which produced "Invalid Date" here previously.
      const startTime = session.date
        ? formatTime(`${session.date}T${session.start_time}`)
        : 'Time TBA';
      const endTime = session.date && session.end_time
        ? formatTime(`${session.date}T${session.end_time}`)
        : 'Time TBA';
      const date = session.date ? formatDate(`${session.date}T00:00`) : 'Date TBA';
      const speakers = session.presenters?.length ? session.presenters.join(', ') : 'TBA';

      return `${date} ${startTime}-${endTime}: ${session.title}\nSpeakers: ${speakers}\nLocation: ${session.location || 'TBA'}\n`;
    }).join('\n');

    const blob = new Blob([`Communication Matters Conference - My Agenda\n\n${agendaText}`], {
      type: 'text/plain'
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'my-agenda.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    announce('Agenda exported as text file');
  };

  // Get unique days for filter
  // Ensure bookmarkedSessions is always a Set to prevent TypeError
  const safeBookmarkedSessions = bookmarkedSessions instanceof Set ? bookmarkedSessions : new Set();
  
  const uniqueDays = [...new Set(sessions
    .filter(session => safeBookmarkedSessions.has(session.id))
    .map(session => session.date)
    .filter(Boolean)
  )].sort();

  const formatTime = (dateString) => {
    if (!dateString) return 'Time TBA';
    
    // Parse the datetime string as local time to avoid timezone conversion
    let date;
    if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
      // Parse as local time by manually extracting components
      const [datePart, timePart] = dateString.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = timePart.split(':').map(Number);
      date = new Date(year, month - 1, day, hour, minute);
    } else {
      date = new Date(dateString);
    }

    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBA';
    
    // Parse the datetime string as local time to avoid timezone conversion
    let date;
    if (typeof dateString === 'string' && dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
      // Parse as local time by manually extracting components
      const [datePart, timePart] = dateString.split('T');
      if (datePart && datePart.includes('-')) {
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart ? timePart.split(':').map(Number) : [0, 0];
        date = new Date(year, month - 1, day, hour, minute);
      } else {
        console.warn('⚠️ Invalid datePart:', datePart);
        date = new Date(dateString);
      }
    } else {
      date = new Date(dateString);
    }

    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'Date TBA';
    
    // Parse the datetime string as local time to avoid timezone conversion
    let date;
    if (typeof dateString === 'string' && dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
      // Parse as local time by manually extracting components
      const [datePart, timePart] = dateString.split('T');
      if (datePart && datePart.includes('-')) {
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart ? timePart.split(':').map(Number) : [0, 0];
        date = new Date(year, month - 1, day, hour, minute);
      } else {
        console.warn('⚠️ Invalid datePart in formatDateShort:', datePart);
        date = new Date(dateString);
      }
    } else {
      date = new Date(dateString);
    }

    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const groupSessionsByDay = () => {
    const grouped = {};
    agendaSessions.forEach(session => {
      
      // Use the transformed date field from fetchSessions if available
      let dateString;
      if (session.date) {
        // Use the pre-processed date from fetchSessions
        dateString = session.date;
      } else if (session.sessionDate) {
        // Fallback to sessionDate if date is not available
        dateString = session.sessionDate;
      } else {
        // Fallback to parsing start_time if transformed dates are not available
        console.warn('⚠️ No transformed date fields available, falling back to start_time parsing for session:', session.id);
        let date;
        if (session.start_time && typeof session.start_time === 'string' && session.start_time.includes('T') && !session.start_time.includes('Z') && !session.start_time.includes('+')) {
          // Parse as local time by manually extracting components
          const [datePart, timePart] = session.start_time.split('T');
          if (datePart && datePart.includes('-') && timePart && timePart.includes(':')) {
            const [year, month, day] = datePart.split('-').map(Number);
            const [hour, minute] = timePart.split(':').map(Number);
            date = new Date(year, month - 1, day, hour, minute);
          } else {
            console.warn('⚠️ Invalid date format in groupSessionsByDay:', session.start_time);
            date = new Date(session.start_time);
          }
        } else if (session.start_time) {
          date = new Date(session.start_time);
        } else {
          // Fallback for sessions without start_time
          console.warn('⚠️ Session missing start_time:', session);
          date = new Date(); // Use current date as fallback
        }
        dateString = date.toDateString();
      }

      if (!grouped[dateString]) {
        grouped[dateString] = [];
      }
      grouped[dateString].push(session);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: 'var(--spacing-xl) var(--spacing-md)' }}>
        <div className="loading-content" role="status" aria-live="polite">
          <h1>Loading Your Agenda...</h1>
          <div className="loading-spinner" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (bookmarkedSessions.size === 0) {
    return (
      <div className="my-agenda-page">
        <div className="container">
          <header className="page-header">
            <h1>My Agenda</h1>
            <p className="page-description">
              Your personal conference schedule
            </p>
          </header>

          <div className="empty-agenda">
            <div className="empty-content">
              <h2>Your agenda is empty</h2>
              <p>
                Start building your personal conference schedule by bookmarking sessions
                you want to attend.
              </p>
              <Link to="/programme" className="btn btn-primary">
                Browse Programme
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const groupedSessions = groupSessionsByDay();

  return (
    <div className="my-agenda-page">
      <div className="container">
        <header className="page-header">
          <h1>My Agenda</h1>
          <p className="page-description">
            Your personal conference schedule with {agendaSessions.length} session{agendaSessions.length !== 1 ? 's' : ''}
          </p>
        </header>

        {/* Controls */}
        <section className="agenda-controls" aria-labelledby="controls-title">
          <h2 id="controls-title" className="sr-only">Agenda Controls</h2>

          <div className="controls-grid">
            {/* View Mode Toggle */}
            <div className="control-group">
              <fieldset className="view-mode-toggle">
                <legend className="control-label">View Mode</legend>
                <div className="toggle-buttons">
                  <button
                    onClick={() => setViewMode('schedule')}
                    className={`btn ${viewMode === 'schedule' ? 'btn-primary' : 'btn-outline'}`}
                    aria-pressed={viewMode === 'schedule'}
                  >
                    📅 Schedule View
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline'}`}
                    aria-pressed={viewMode === 'list'}
                  >
                    📋 List View
                  </button>
                </div>
              </fieldset>
            </div>

            {/* Day Filter — button pills, not a <select>, so the control
                can't retain keyboard focus and silently swallow the N/P
                list-navigation shortcuts the way a native select does
                (matches Programme's Quick Day Filter pattern). */}
            <div className="control-group">
              <span className="control-label">Filter by Day</span>
              <div className="quick-day-filters">
                <button
                  type="button"
                  className={`day-filter-btn ${selectedDay === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedDay('all')}
                >
                  All Days
                </button>
                {uniqueDays.map(day => (
                  <button
                    type="button"
                    key={day}
                    className={`day-filter-btn ${selectedDay === day ? 'active' : ''}`}
                    onClick={() => setSelectedDay(selectedDay === day ? 'all' : day)}
                  >
                    {new Date(`${day}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </button>
                ))}
              </div>
            </div>

            {/* Export Button */}
            <div className="control-group">
              <AacButton
                onClick={exportAgenda}
                variant="secondary"
                shortcut={1}
                disabled={agendaSessions.length === 0}
              >
                📄 Export Agenda
              </AacButton>
            </div>
          </div>

          {/* Results Info */}
          <div className="results-info" role="status" aria-live="polite">
            {selectedDay === 'all'
              ? `Showing all ${agendaSessions.length} sessions in your agenda`
              : `Showing ${agendaSessions.length} sessions for ${new Date(`${selectedDay}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`
            }
          </div>
        </section>

        {/* Agenda Content */}
        <section className="agenda-content" aria-labelledby="agenda-title">
          <h2 id="agenda-title" className="sr-only">Your Sessions</h2>

          {agendaSessions.length === 0 ? (
            <div className="no-sessions">
              <h3>No sessions for selected day</h3>
              <p>Try selecting a different day or view all days.</p>
              <button
                onClick={() => setSelectedDay('all')}
                className="btn btn-primary"
              >
                Show All Days
              </button>
            </div>
          ) : viewMode === 'schedule' ? (
            <div className="schedule-view">
              {Object.entries(groupedSessions).map(([date, daySessions]) => (
                <div key={date} className="day-schedule">
                  <h3 className="day-title">
                    {new Date(date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </h3>

                  <div className="day-sessions">
                    {daySessions.map((session, sessionIndex) => {
                      const nextSession = daySessions[sessionIndex + 1];

                      // Parse end time as local time
                      let currentEnd;
                      if (session.end_time && session.end_time.includes('T') && !session.end_time.includes('Z') && !session.end_time.includes('+')) {
                        const [datePart, timePart] = session.end_time.split('T');
                        const [year, month, day] = datePart.split('-').map(Number);
                        const [hour, minute] = timePart.split(':').map(Number);
                        currentEnd = new Date(year, month - 1, day, hour, minute);
                      } else if (session.end_time) {
                        currentEnd = new Date(session.end_time);
                      } else {
                        // Fallback if end_time is null - assume 45 minutes after start
                        if (session.start_time) {
                          const startTime = new Date(session.start_time);
                          currentEnd = new Date(startTime.getTime() + 45 * 60 * 1000);
                        } else {
                          // If both start_time and end_time are null, use current time as fallback
                          currentEnd = new Date();
                        }
                      }

                      // Parse next session start time as local time
                      let nextStart = null;
                      if (nextSession && nextSession.start_time) {
                        if (nextSession.start_time.includes('T') && !nextSession.start_time.includes('Z') && !nextSession.start_time.includes('+')) {
                          const [datePart, timePart] = nextSession.start_time.split('T');
                          const [year, month, day] = datePart.split('-').map(Number);
                          const [hour, minute] = timePart.split(':').map(Number);
                          nextStart = new Date(year, month - 1, day, hour, minute);
                        } else {
                          nextStart = new Date(nextSession.start_time);
                        }
                      }

                      const hasGap = nextStart && (nextStart - currentEnd) > 15 * 60 * 1000; // 15+ minute gap

                      const isCurrentNavSession = currentNavSession && currentNavSession.id === session.id;

                      return (
                        <React.Fragment key={session.id}>
                          <article
                            ref={(el) => {
                              if (el) {
                                sessionRefs.current.set(session.id, el);
                              } else {
                                sessionRefs.current.delete(session.id);
                              }
                            }}
                            tabIndex={-1}
                            className={`schedule-session ${session.status === 'cancelled' ? 'cancelled' : ''} ${isCurrentNavSession ? 'list-nav-current' : ''}`}
                          >
                            <div className="session-time">
                              {session.time || 'Time TBA'}
                            </div>

                            <div className="session-details">
                              <h4 className={`session-title ${session.status === 'cancelled' ? 'cancelled-session' : ''}`}>
                                <Link to={`/session/${session.id}`}>
                                  {session.title}
                                  {session.status === 'cancelled' && <span className="cancelled-indicator"> (CANCELLED)</span>}
                                </Link>
                              </h4>
                              {session.status === 'cancelled' && (
                                <div className="cancellation-notice">
                                  <p><strong>⚠️ This session has been cancelled</strong></p>
                                  {session.cancellation_notes && (
                                    <p className="cancellation-notes">{session.cancellation_notes}</p>
                                  )}
                                </div>
                              )}

                              <div className="session-meta">
                                {session.location && (
                                  <span className="session-location">
                                    📍 {session.location}
                                  </span>
                                )}
                                {session.track && (
                                  <span className="session-track">
                                    {session.track}
                                  </span>
                                )}
                              </div>

                              {session.presenters && session.presenters.length > 0 && (
                                <div className="session-speakers">
                                  {session.presenters.map((presenter, idx) => (
                                    <span key={`speaker-${idx}`} className="speaker-name">
                                      {presenter}
                                      {idx < session.presenters.length - 1 && ', '}
                                    </span>
                                  ))}
                                </div>
                              )}

                              <div className="session-actions">
                                <Link
                                  to={`/session/${session.id}`}
                                  className="btn btn-sm btn-primary"
                                >
                                  View Details
                                </Link>
                                <button
                                  onClick={() => removeFromAgenda(session.id)}
                                  className="btn btn-sm btn-outline"
                                  title="Remove from agenda"
                                >
                                  ❌ Remove
                                </button>
                              </div>
                            </div>
                          </article>

                          {hasGap && (
                            <div className="schedule-gap">
                              <span className="gap-text">
                                {Math.round((nextStart - currentEnd) / (60 * 1000))} minute break
                              </span>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="list-view">
              {agendaSessions.map((session) => {
                const isCurrentNavSession = currentNavSession && currentNavSession.id === session.id;
                return (
                <article
                  key={session.id}
                  ref={(el) => {
                    if (el) {
                      sessionRefs.current.set(session.id, el);
                    } else {
                      sessionRefs.current.delete(session.id);
                    }
                  }}
                  tabIndex={-1}
                  className={`list-session ${session.status === 'cancelled' ? 'cancelled' : ''} ${isCurrentNavSession ? 'list-nav-current' : ''}`}
                >
                  <div className="session-time">
                    <time dateTime={session.start_time}>
                      <span className="session-date">
                        {session.date ? formatDateShort(session.date) : (session.sessionDate ? formatDateShort(session.sessionDate) : 'Date TBA')}
                      </span>
                      <span className="session-time-range">
                        {session.time || 'Time TBA'}
                      </span>
                    </time>
                    {session.location && (
                      <div className="session-location">
                        📍 {session.location}
                      </div>
                    )}
                  </div>

                  <div className="session-content">
                    <div className="session-header">
                      <h3 className={`session-title ${session.status === 'cancelled' ? 'cancelled-session' : ''}`}>
                        <Link to={`/session/${session.id}`}>
                          {session.title}
                          {session.status === 'cancelled' && <span className="cancelled-indicator"> (CANCELLED)</span>}
                        </Link>
                      </h3>
                      {session.status === 'cancelled' && (
                        <div className="cancellation-notice">
                          <p><strong>⚠️ This session has been cancelled</strong></p>
                          {session.cancellation_notes && (
                            <p className="cancellation-notes">{session.cancellation_notes}</p>
                          )}
                        </div>
                      )}
                      {session.track && (
                        <span className="session-track">{session.track}</span>
                      )}
                    </div>

                    {session.presenters && session.presenters.length > 0 && (
                      <div className="session-speakers">
                        <span className="speakers-label">Speakers:</span>
                        {session.presenters.map((presenter, index) => (
                          <span key={`speaker-${index}`} className="speaker-name">
                            {presenter}
                            {index < session.presenters.length - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="session-actions">
                      <Link
                        to={`/session/${session.id}`}
                        className="btn btn-primary"
                      >
                        View Details
                      </Link>

                      <button
                        onClick={() => removeFromAgenda(session.id)}
                        className="btn btn-outline"
                        title="Remove from agenda"
                      >
                        ❌ Remove from Agenda
                      </button>
                    </div>
                  </div>
                </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

    </div>
  );
};

export default MyAgenda;
